import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomTooltip from 'Components/Tooltip/CustomTooltip';
import Loader from 'Components/Utility/Loader/Loader';
import { createSystemCanvasControlType } from '../Store/CustomModule.action';
import { getControlTypeById } from 'services/custom-module.services';
import '../system-canvas-cm.css';
import '../CustomModule.css';
import { SystemCanvasControlsListProps, ControlCard, ControlTypeResponse, ApiResponseWrapper, ControlTypeData, ControlConfigValue } from './types';
import {
  CheckboxConfig,
  TextBoxConfig,
  DropdownConfig,
  RadioConfig,
  DatePickerConfig,
  CommentBoxConfig,
  StatusConfig,
  SourceConfig,
  TableColumnsEditor,
} from 'Components/Controls/cm-control-config';
import {
  CheckboxConfigValue,
  TextBoxConfigValue,
  DropdownConfigValue,
  RadioConfigValue,
  DatePickerConfigValue,
  CommentBoxConfigValue,
  StatusConfigValue,
  SourceConfigValue,
} from 'Components/Controls/cm-control-config/types';
import { FieldItem } from 'Components/Controls/cm-control-config/types';
import { createOption } from 'Components/Controls/cm-control-config';
import { toasterMsg } from 'Components/Toaster/Toaster';
import ConfirmPopup from 'Components/ModalPopup/confirm-popup';
import { shortenStringWithTooltip } from 'Components/helper';
import SectionConfigBody from '../Library/components/section-config-body';
import SubSectionConfigBody from '../Library/components/sub-section-config-body';
import type { CMSection, CMSubSection, CMField, CMTable } from '../Library/add-edit.types';
import { CloseIcon, DeleteIcon, DuplicateIcon, InfoIcon, LibraryIcon, RequiredAlertIcon } from '../../../../bombiix-icon';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { NewInput } from '../../../../Components/PIM/common/NewInput';
import { Floaty, FloatyButton } from '../../../../Components/Floaty';

/**
 * SystemCanvasControlsList component displays a list of system canvas controls with
 * selection, deletion, duplication, and inline editing capabilities.
 * Supports complex control types including sections, subsections, and various field types
 * with full configuration editing through modal interfaces.
 * @param cards - Array of control cards to display
 * @param loading - Loading state indicator
 * @param selectedIds - Object containing selected card IDs
 * @param onToggleOne - Callback for toggling individual card selection
 * @param onDeleteControl - Callback for deleting a control
 * @param onRefetch - Callback for refetching data after operations
 */
const getNextVersionTitle = (baseName: string, allCards: ControlCard[]): string => {
  const escaped = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^${escaped} V(\\d+)$`);
  let maxVersion = 0;
  allCards.forEach((c) => {
    const match = c.title?.match(pattern);
    if (match && match[1]) {
      const n = parseInt(match[1], 10);
      if (n > maxVersion) maxVersion = n;
    }
  });
  return `${baseName} V${maxVersion + 1}`;
};

const SystemCanvasControlsList: React.FC<SystemCanvasControlsListProps> = ({ cards, loading, selectedIds, onToggleOne, onDeleteControl, onRefetch, onCreateLibrary }) => {
  const { t } = useTranslation(['common', 'custom-module-system-canvas', 'control-library']);
  const dispatch = useDispatch();
  const [showDuplicateOffcanvas, setShowDuplicateOffcanvas] = useState(false);
  const [controlToDuplicate, setControlToDuplicate] = useState<{ id: string; name: string; type: string; suggestedTitle: string } | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [controlConfig, setControlConfig] = useState<ControlConfigValue | null>(null);
  const [controlTitle, setControlTitle] = useState<string>('');
  const [controlType, setControlType] = useState<string>('');
  const [editingTablePath, setEditingTablePath] = useState<{ subIndex: number; colIndex: number } | null>(null);
  const [editingFieldPath, setEditingFieldPath] = useState<{ subIndex: number; fieldIndex: number } | null>(null);
  const [tempSections, setTempSections] = useState<CMSection[] | null>(null);
  const [_openRenderedSections, setOpenRenderedSections] = useState<number[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [_openRenderedSubSections, setOpenRenderedSubSections] = useState<string[]>([]);

  const selectedTableIds = useMemo(
    () =>
      cards
        .filter((c) => c.controlType === 'table' && selectedIds[c.id])
        .map((c) => c.id),
    [cards, selectedIds],
  );

  useEffect(() => {
    if (showDuplicateOffcanvas && controlToDuplicate) {
      setFetchLoading(true);
      setFetchError(null);
      setControlConfig(null);
      setControlTitle('');
      setControlType('');

      const fetchControlData = async () => {
        try {
          const res = (await getControlTypeById(controlToDuplicate.id)) as unknown as ApiResponseWrapper<ControlTypeResponse>;

          if (res && res?.code === 200 && res?.data) {
            const controlData = res.data;
            const content = controlData?.content;

            if (content?.type && content?.content) {
              const configContent = content.content as ControlConfigValue;
              setControlConfig(configContent);
              setControlTitle(controlToDuplicate.suggestedTitle);
              setControlType(content.type);

              if (content.type === 'section') {
                const section = configContent as CMSection;
                setTempSections([section]);
                setOpenRenderedSections(section.isAccordion ? [0] : []);
              } else if (content.type === 'subSection' || content.type === 'subsection') {
                const subsection = configContent as CMSubSection;
                const wrapperSection: CMSection = {
                  label: 'Section 1',
                  title: '',
                  isVisibleToSupplier: true,
                  content: [subsection],
                };
                setTempSections([wrapperSection]);
                setOpenRenderedSections([0]);
              } else {
                setTempSections(null);
                setOpenRenderedSections([]);
              }

              setFetchLoading(false);
            } else {
              const errorMsg = t('CUSTOM_MODULE.INVALID_CONTROL_DATA', { ns: 'custom-module-system-canvas' });
              setFetchError(errorMsg);
              setFetchLoading(false);
              toasterMsg(errorMsg, 'danger');
            }
          } else {
            const errorMsg =
              res?.message || res?.data?.errMsg || t('CUSTOM_MODULE.CONTROL_DATA_NOT_FOUND', { ns: 'custom-module-system-canvas' }) || 'Control data not found';
            setFetchError(errorMsg);
            setFetchLoading(false);
            toasterMsg(errorMsg, 'danger');
          }
        } catch (err) {
          setFetchLoading(false);
          const error = err as Error;
          const errorMsg = error?.message || t('CUSTOM_MODULE.FAILED_TO_LOAD_CONTROL_DATA', { ns: 'custom-module-system-canvas' });
          setFetchError(errorMsg);
          toasterMsg(errorMsg, 'danger');
        }
      };

      fetchControlData();
    }
  }, [showDuplicateOffcanvas, controlToDuplicate, t]);

  /**
   * Initiates the duplication process for a control
   * @param controlId - ID of the control to duplicate
   * @param controlName - Name of the control for confirmation display
   * @param controlType - Type of the control
   */
  const handleDuplicateClick = useCallback(
    (controlId: string, controlName: string, controlType: string) => {
      const suggestedTitle = getNextVersionTitle(controlName, cards);
      setControlToDuplicate({ id: controlId, name: controlName, type: controlType, suggestedTitle });
      setShowDuplicateOffcanvas(true);
    },
    [cards],
  );

  /**
   * Closes the duplication offcanvas and resets all related state
   */
  const handleCloseOffcanvas = useCallback(() => {
    setShowDuplicateOffcanvas(false);
    setControlToDuplicate(null);
    setControlConfig(null);
    setControlTitle('');
    setControlType('');
    setFetchError(null);
    setFetchLoading(false);
    setEditingTablePath(null);
    setEditingFieldPath(null);
    setTempSections(null);
    setOpenRenderedSections([]);
    setOpenRenderedSubSections([]);
  }, []);

  /**
   * Handles configuration changes from the control editor
   * @param newConfig - The updated configuration object
   */
  const handleConfigChange = useCallback((newConfig: ControlConfigValue) => {
    setControlConfig(newConfig);
  }, []);

  const toFieldItem = useCallback((f: CMField, index: number): FieldItem | null => {
    switch (f.type) {
      case 'dorpdown':
        return {
          id: `f_${index}`,
          type: 'dropdown',
          convertToAccordion: !!f.isAccordion,
          config: {
            title: f.title,
            required: f.isRequired,
            multiSelect: !!f.isMultiple,
            options: (f.options || []).map((l: string) => createOption(l)),
          } as DropdownConfigValue,
        };
      case 'text':
        return {
          id: `f_${index}`,
          type: 'text',
          convertToAccordion: !!f.isAccordion,
          config: { title: f.title, required: f.isRequired } as TextBoxConfigValue,
        };
      case 'checkbox':
        return {
          id: `f_${index}`,
          type: 'checkbox',
          convertToAccordion: !!f.isAccordion,
          config: { title: f.title, required: f.isRequired, options: (f.options || []).map((l: string) => createOption(l)) } as CheckboxConfigValue,
        };
      case 'radio':
        return {
          id: `f_${index}`,
          type: 'radio',
          convertToAccordion: !!f.isAccordion,
          config: { title: f.title, required: f.isRequired, options: (f.options || []).map((l: string) => createOption(l)) } as RadioConfigValue,
        };
      case 'date-picker':
        return {
          id: `f_${index}`,
          type: 'date',
          convertToAccordion: !!f.isAccordion,
          config: {
            title: f.title,
            required: f.isRequired,
            range: !!f.range,
            minDate: f.minDate || '',
            maxDate: f.maxDate || '',
          } as DatePickerConfigValue,
        };
      case 'comment':
        return {
          id: `f_${index}`,
          type: 'comment',
          convertToAccordion: !!f.isAccordion,
          config: { title: f.title },
        };
      case 'status':
        return {
          id: `f_${index}`,
          type: 'status',
          convertToAccordion: !!f.isAccordion,
          config: {
            title: f.title ?? '',
            statusColours: f.statusColours ?? [],
          } as StatusConfigValue,
        };
      case 'source':
        return {
          id: `f_${index}`,
          type: 'source',
          convertToAccordion: !!f.isAccordion,
          config: {
            title: f.title ?? '',
            required: !!f.isRequired,
            relation: (f.sourceRelation as unknown) ?? null,
            isVisibleToSupplier: !!f.isVisibleToSupplier,
          },
        };
      default:
        return null;
    }
  }, []);

  const applyConfigToField = useCallback((field: CMField, cfg: unknown): CMField => {
    const cfgAcc = cfg as Partial<{ convertToAccordion: boolean; width?: string }>;
    const mergeWidth = (result: CMField): CMField => {
      const configuredWidth = cfgAcc.width;
      return typeof configuredWidth === 'string' ? { ...result, width: configuredWidth.trim() } : result;
    };
    switch (field.type) {
      case 'dorpdown': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as DropdownConfigValue).title,
          isRequired: !!(cfg as DropdownConfigValue).required,
          isMultiple: !!(cfg as DropdownConfigValue).multiSelect,
          options: ((cfg as DropdownConfigValue).options || []).map((o: { label: string }) => o.label as string),
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'text': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as TextBoxConfigValue).title,
          isRequired: !!(cfg as TextBoxConfigValue).required,
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'checkbox': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as CheckboxConfigValue).title,
          isRequired: !!(cfg as CheckboxConfigValue).required,
          options: ((cfg as CheckboxConfigValue).options || []).map((o: { label: string }) => o.label as string),
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'radio': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as RadioConfigValue).title,
          isRequired: !!(cfg as RadioConfigValue).required,
          options: ((cfg as RadioConfigValue).options || []).map((o: { label: string }) => o.label as string),
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'date-picker': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as DatePickerConfigValue).title,
          isRequired: !!(cfg as DatePickerConfigValue).required,
          range: !!(cfg as DatePickerConfigValue).range,
          minDate: (cfg as DatePickerConfigValue).minDate || null,
          maxDate: (cfg as DatePickerConfigValue).maxDate || null,
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'comment': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as CommentBoxConfigValue).title,
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'status': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as StatusConfigValue).title ?? '',
          statusColours: (cfg as StatusConfigValue).statusColours ?? [],
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      case 'source': {
        const nextAcc = cfgAcc.convertToAccordion ?? field.isAccordion;
        return mergeWidth({
          ...field,
          title: (cfg as SourceConfigValue).title ?? '',
          isRequired: !!(cfg as SourceConfigValue).required,
          sourceRelation: (cfg as SourceConfigValue).relation as unknown,
          isVisibleToSupplier: !!(cfg as SourceConfigValue).isVisibleToSupplier,
          ...(nextAcc !== undefined ? { isAccordion: nextAcc } : {}),
        });
      }
      default:
        return mergeWidth(field);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!controlToDuplicate || !controlConfig || !controlType) return;

    const finalTitle = controlTitle || controlToDuplicate.suggestedTitle;

    if (!finalTitle.trim()) {
      toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROL_TITLE_REQUIRED'), 'danger');
      return;
    }

    const type = controlType.toLowerCase();
    if (['radio', 'checkbox', 'dropdown'].includes(type)) {
      const options = (controlConfig as CheckboxConfigValue | RadioConfigValue | DropdownConfigValue)?.options;

      if (!options || !Array.isArray(options) || options.length === 0) {
        toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.OPTIONS_REQUIRED'), 'danger');
        return;
      }

      const hasValidOptions = options.some((opt: { label?: string; id?: string }) => {
        if (!opt || typeof opt !== 'object') return false;
        const label = opt?.label || '';
        return typeof label === 'string' && label.trim().length > 0;
      });

      if (!hasValidOptions) {
        toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.OPTIONS_MUST_HAVE_VALUE'), 'danger');
        return;
      }
    }

    setDuplicateLoading(true);
    try {
      const normalizedType = type === 'subsection' ? 'subSection' : controlType;
      const controlTypeData: ControlTypeData = {
        title: finalTitle.trim(),
        content: {
          type: normalizedType,
          content: controlConfig,
        },
      };

      const response = (await dispatch(createSystemCanvasControlType(controlTypeData, t))) as unknown as ApiResponseWrapper;

      if (response && response.code === 200) {
        toasterMsg(t('SYSTEM_CANVAS.BACKEND_CONTROLLER.CUSTOM_MODULE_CONTROL_DUPLICATED', { ns: 'custom-module-system-canvas' }), 'success');
        handleCloseOffcanvas();
        if (onRefetch) {
          onRefetch();
        }
      } else {
        const errorMsg =
          (response?.data as { errMsg?: string })?.errMsg ||
          response?.message ||
          t('CUSTOM_MODULE.FAILED_TO_DUPLICATE_CONTROL', { ns: 'custom-module-system-canvas' });
        toasterMsg(errorMsg, 'danger');
      }
    } catch (error) {
      const err = error as Error & { response?: { data?: { errMsg?: string; message?: string } } };
      const errorMsg =
        err?.message ||
        err?.response?.data?.errMsg ||
        err?.response?.data?.message ||
        t('CUSTOM_MODULE.FAILED_TO_DUPLICATE_CONTROL', { ns: 'custom-module-system-canvas' });
      toasterMsg(errorMsg, 'danger');
    } finally {
      setDuplicateLoading(false);
    }
  }, [controlToDuplicate, controlConfig, controlType, controlTitle, t, dispatch, onRefetch, handleCloseOffcanvas]);

  const normalizedControlType = useMemo(() => controlType?.toLowerCase() ?? '', [controlType]);

  const renderConfigUI = useCallback(() => {
    if (!controlConfig || !controlType) return null;

    const type = normalizedControlType;

    switch (type) {
      case 'checkbox':
        return <CheckboxConfig value={controlConfig as CheckboxConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'text':
        return <TextBoxConfig value={controlConfig as TextBoxConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'dropdown':
        return <DropdownConfig value={controlConfig as DropdownConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'radio':
        return <RadioConfig value={controlConfig as RadioConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'date':
      case 'date-picker':
        return <DatePickerConfig value={controlConfig as DatePickerConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'comment':
        return <CommentBoxConfig value={controlConfig as CommentBoxConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'status':
        return <StatusConfig value={controlConfig as StatusConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'source':
        return <SourceConfig value={controlConfig as SourceConfigValue} onChange={handleConfigChange} showLibraryTitle={false} />;
      case 'table': {
        const table = controlConfig as CMTable;
        const setTable = (updater: (t: CMTable) => CMTable) => {
          setControlConfig((prev) => updater({ ...(prev as CMTable) }));
        };
        return (
          <TableColumnsEditor
            table={table}
            setTable={setTable}
            subIndex={0}
            editingTablePath={editingTablePath}
            setEditingTablePath={setEditingTablePath}
            toFieldItem={toFieldItem}
            applyConfigToField={applyConfigToField}
            showLibraryTitle={false}
          />
        );
      }
      case 'section': {
        const section = controlConfig as CMSection;
        const sections = tempSections ?? [section];
        const effectiveSections = tempSections ?? [section];
        const activeSectionIndex = 0;

        const setToolbarSections: React.Dispatch<React.SetStateAction<CMSection[]>> = (updater) => {
          setTempSections((prev) => {
            const base = prev ?? [section];
            if (typeof updater === 'function') {
              const updated = updater(base);
              if (updated && updated.length > 0 && updated[0]) {
                setControlConfig(updated[0]);
              }
              return updated;
            }
            if (updater && updater.length > 0 && updater[0]) {
              setControlConfig(updater[0]);
            }
            return updater;
          });
        };

        let canShowAddSubSectionButton = false;
        if (activeSectionIndex !== null) {
          const sec = effectiveSections[activeSectionIndex];
          if (sec) {
            const contentArray: CMSubSection[] = (sec.content as CMSubSection[]) || [];
            const lastSubIndex = [...contentArray]
              .map((contentItem) => contentItem && 'content' in contentItem && !('columns' in (contentItem as unknown as CMTable)))
              .lastIndexOf(true);
            if (lastSubIndex !== -1) {
              const lastSub = contentArray[lastSubIndex] as CMSubSection;
              const inner = Array.isArray(lastSub?.content) ? (lastSub.content as Array<CMField | CMTable>) : [];
              canShowAddSubSectionButton = inner.length > 0;
            }
          }
        }

        const addSubSectionToActive = () => {
          if (activeSectionIndex === null) return;
          setTempSections((prev) => {
            const base = (prev ?? sections).map((s: CMSection) => ({ ...s }));
            return base.map((s: CMSection, i: number) => {
              if (i !== activeSectionIndex) return s;
              const c: CMSubSection[] = s.content ?? [];
              const subsCount = c.filter((x) => x && 'content' in x && !('columns' in (x as unknown as CMTable))).length;
              const newSub: CMSubSection = { type: 'subSection', label: `Sub Section ${subsCount + 1}`, title: '', content: [] };
              return { ...s, content: [...c, newSub] };
            });
          });
        };

        const handleRequestDeleteSubSection = () => {
          toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.CANNOT_DELETE_SUBSECTION_IN_COPY'), 'warning');
        };

        return (
          <SectionConfigBody
            activeSectionIndex={activeSectionIndex}
            effectiveSections={effectiveSections}
            sections={sections}
            tempSections={tempSections}
            setTempSections={(updater) => {
              if (typeof updater === 'function') {
                setTempSections((prev) => {
                  const base = prev ?? [section];
                  const updated = updater(base);
                  if (updated && updated.length > 0 && updated[0]) {
                    setControlConfig(updated[0]);
                  }
                  return updated;
                });
              } else {
                setTempSections(updater);
                if (updater && updater.length > 0 && updater[0]) {
                  setControlConfig(updater[0]);
                }
              }
            }}
            setOpenRenderedSections={setOpenRenderedSections}
            setOpenRenderedSubSections={setOpenRenderedSubSections}
            requestDeleteSubSection={handleRequestDeleteSubSection}
            editingFieldPath={editingFieldPath}
            setEditingFieldPath={setEditingFieldPath}
            editingTablePath={editingTablePath}
            setEditingTablePath={setEditingTablePath}
            toFieldItem={toFieldItem}
            applyConfigToField={applyConfigToField}
            setToolbarSections={setToolbarSections}
            canShowAddSubSectionButton={canShowAddSubSectionButton}
            addSubSectionToActive={addSubSectionToActive}
            showLibraryTitle={false}
          />
        );
      }
      case 'subsection':
      case 'subSection': {
        const subsection = controlConfig as CMSubSection;
        const tempSubsection =
          tempSections && tempSections.length > 0 && tempSections[0]?.content && tempSections[0].content.length > 0
            ? (tempSections[0].content[0] as CMSubSection)
            : subsection;

        const handleRequestDeleteSubSection = () => {
          toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.CANNOT_DELETE_SUBSECTION_IN_COPY'), 'warning');
        };

        return (
          <SubSectionConfigBody
            subsection={tempSubsection}
            onUpdate={(updatedSubsection) => {
              setControlConfig(updatedSubsection as ControlConfigValue);
              const wrapperSection: CMSection = {
                label: 'Section 1',
                title: '',
                isVisibleToSupplier: true,
                content: [updatedSubsection],
              };
              setTempSections([wrapperSection]);
            }}
            onDelete={handleRequestDeleteSubSection}
            editingFieldPath={editingFieldPath ? { ...editingFieldPath, subIndex: 0 } : null}
            setEditingFieldPath={(updater) => {
              if (typeof updater === 'function') {
                const current = editingFieldPath ? { ...editingFieldPath, subIndex: 0 } : null;
                const updated = updater(current);
                setEditingFieldPath(updated);
              } else {
                setEditingFieldPath(updater);
              }
            }}
            editingTablePath={editingTablePath ? { ...editingTablePath, subIndex: 0 } : null}
            setEditingTablePath={(updater) => {
              if (typeof updater === 'function') {
                const current = editingTablePath ? { ...editingTablePath, subIndex: 0 } : null;
                const updated = updater(current);
                setEditingTablePath(updated);
              } else {
                setEditingTablePath(updater);
              }
            }}
            toFieldItem={toFieldItem}
            applyConfigToField={applyConfigToField}
            showCommentBtn={true}
            subIndex={0}
            showLibraryTitle={false}
          />
        );
      }
      default:
        return <div>{t('CUSTOM_MODULE.UNSUPPORTED_CONTROL_TYPE', { ns: 'custom-module-system-canvas' })}</div>;
    }
  }, [controlConfig, controlType, normalizedControlType, tempSections, editingTablePath, editingFieldPath, toFieldItem, applyConfigToField, t]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setControlTitle(e.target.value);
  }, []);

  const duplicateTooltipText = useMemo(() => t('COMMON_LABEL.DUPLICATE'), [t]);
  const deleteTooltipText = useMemo(() => t('COMMON_LABEL.DELETE'), [t]);
  const createdDateLabel = useMemo(() => t('CUSTOM_MODULE.INFO.CREATED_DATE', { ns: 'custom-module-system-canvas' }), [t]);
  const modifiedDateLabel = useMemo(() => t('CUSTOM_MODULE.INFO.MODIFIED_DATE', { ns: 'custom-module-system-canvas' }), [t]);
  const controlTypeLabel = useMemo(() => t('CUSTOM_MODULE.CONTROL.TYPE', { ns: 'custom-module-system-canvas' }), [t]);
  const infoAriaLabel = useMemo(() => t('CUSTOM_MODULE.SYSTEM_CANVAS.TABLE_ACTIONS.INFORMATION', { ns: 'custom-module-system-canvas' }), [t]);

  const content = useMemo(() => {
    if (loading) {
      return <Loader />;
    }

    if (cards.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="icon-Control f-48 text-muted mb-3" />
          <h5>{t('CUSTOM_MODULE.NO_CONTROLS_FOUND', { ns: 'custom-module-system-canvas' })}</h5>
          <p className="text-muted">{t('CUSTOM_MODULE.CREATE_FIRST_CONTROL', { ns: 'custom-module-system-canvas' })}</p>
        </div>
      );
    }

    return (
      <div className="cm-card-grid">
        {cards.map((card: ControlCard) => {
          return (
            <div key={card.id} className="cm-card-grid-item item-list">
              <div className="cm-listing-card card h-100">
                <div className="card-header d-flex align-items-center">
                  {card.controlType === 'table' && (
                    <NewCheckbox
                      className="mr-2"
                      name={`check_${card.id}`}
                      id={`check_ctrl_${card.id}`}
                      checked={!!selectedIds[card.id]}
                      onChange={() => onToggleOne(card.id)}
                    />
                  )}
                  <ul className=" d-flex align-items-center">
                    <li className="m-0 p-0">
                      <CustomTooltip tooltipText={duplicateTooltipText}>
                        <button
                          type="button"
                          className="btn btn-action-icon m-0 p-0"
                          aria-label={duplicateTooltipText}
                          onClick={() => handleDuplicateClick(card.id, card.title, card.controlType)}
                        >
                          <DuplicateIcon size={24} />
                        </button>
                      </CustomTooltip>
                    </li>
                    <li className="m-0 p-0">
                      <CustomTooltip tooltipText={deleteTooltipText}>
                        <button type="button" className="btn btn-action-icon m-0 p-0" aria-label={deleteTooltipText} onClick={() => onDeleteControl?.(card.id)}>
                          <DeleteIcon size={24} />
                        </button>
                      </CustomTooltip>
                    </li>
                    <li className="m-0 p-0">
                      <CustomTooltip
                        tooltipText={
                          <div className="text-left">
                            <p className="mb-0">
                              <b>{createdDateLabel}:&nbsp;</b>
                              <span>{card.createdAt || 'N/A'}</span>
                            </p>
                            <p className="mb-0">
                              <b>{modifiedDateLabel}:&nbsp;</b>
                              <span>{card.modifiedAt || 'N/A'}</span>
                            </p>
                          </div>
                        }
                      >
                        <button type="button" className="btn btn-action-icon m-0 p-0" aria-label={infoAriaLabel}>
                          <InfoIcon size={24} />
                        </button>
                      </CustomTooltip>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  <h4 className="title">{shortenStringWithTooltip(card.title, 30)}</h4>
                  <ul className="card-ul">
                    <li className="text-truncate">
                      <strong className="text-nowrap mr-1">{controlTypeLabel}:</strong>
                      <span>{card.controlType}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [
    loading,
    cards,
    selectedIds,
    onToggleOne,
    handleDuplicateClick,
    onDeleteControl,
    duplicateTooltipText,
    deleteTooltipText,
    createdDateLabel,
    modifiedDateLabel,
    controlTypeLabel,
    infoAriaLabel,
    selectedTableIds,
    onCreateLibrary,
    t,
  ]);

  return (
    <>
      {content}
      {selectedTableIds.length > 0 && (
        <Floaty className="floting-actions">
          <FloatyButton
            icon={DeleteIcon}
            label={t('DELETE_SELECTED', { ns: 'control-library' })}
            variant="red"
            onClick={() => setPendingDeleteIds([...selectedTableIds])}
          />
          <FloatyButton icon={LibraryIcon} label={t('CREATE_LIBRARY_FROM_SELECTION', { ns: 'control-library' })} onClick={() => onCreateLibrary?.(selectedTableIds)} />
        </Floaty>
      )}
      {showDuplicateOffcanvas && controlToDuplicate && (
        <>
          <div className={`offcanvas-backdrop ${showDuplicateOffcanvas ? 'show' : ''}`} onClick={handleCloseOffcanvas} />
          <div
            className={`offcanvas offcanvas-end ${showDuplicateOffcanvas ? 'show' : ''}`}
            style={{ visibility: showDuplicateOffcanvas ? 'visible' : 'hidden' }}
            tabIndex={-1}
            aria-labelledby="duplicateControlOffcanvasLabel"
            aria-hidden={!showDuplicateOffcanvas}
            role="dialog"
          >
            <div className="offcanvas-header">
              {!fetchError && controlConfig && (
                <button className="bombiix-btn btn-blue" type="button" onClick={handleSave} disabled={duplicateLoading || fetchLoading}>
                  {duplicateLoading ? t('LOADING') : t('COMMON_LABEL.SAVE')}
                </button>
              )}
              {!fetchError && !controlConfig && <div />}
              <h4 className="mx-auto my-0 cm-offcanvas-title" id="duplicateControlOffcanvasLabel">
                {shortenStringWithTooltip(controlTitle || controlToDuplicate.suggestedTitle, 25, 'top')}
              </h4>
              <CloseIcon size={24} className="cursor-pointer" onClick={handleCloseOffcanvas} aria-label={t('COMMON_LABEL.CLOSE')} />
            </div>
            <div className="offcanvas-body">
              {fetchLoading || duplicateLoading ? (
                <Loader />
              ) : fetchError ? (
                <div className="text-center py-5">
                  <RequiredAlertIcon size={48} className="text-danger mb-3" />
                  <h5 className="text-danger">{t('CUSTOM_MODULE.ERROR_LOADING_CONTROL', { ns: 'custom-module-system-canvas' })}</h5>
                  <p className="text-danger">{fetchError}</p>
                  <button className="btn btn-default mt-3" type="button" onClick={handleCloseOffcanvas}>
                    {t('COMMON_LABEL.CLOSE')}
                  </button>
                </div>
              ) : controlConfig && controlType ? (
                <>
                  <div className="mb-3">
                    <label htmlFor="controlTitle" className="mb-0 f-16 font-medium">
                      {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROL_TITLE', { ns: 'custom-module-system-canvas' })} <span className="text-danger">*</span>
                    </label>
                    <NewInput
                      id="controlTitle"
                      type="text"
                      value={controlTitle}
                      onChange={handleTitleChange}
                      placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_CONTROL_TITLE', { ns: 'custom-module-system-canvas' })}
                    />
                  </div>
                  {renderConfigUI()}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
      <ConfirmPopup
        onOpen={pendingDeleteIds.length > 0}
        title={t('DELETE_SELECTED', { ns: 'control-library' })}
        message={`Delete ${pendingDeleteIds.length} selected control(s)?`}
        toggle={() => setPendingDeleteIds([])}
        onConfirm={() => {
          pendingDeleteIds.forEach((id) => onDeleteControl?.(id));
          setPendingDeleteIds([]);
        }}
      />
    </>
  );
};

export default memo(SystemCanvasControlsList);
